import type { TRPCClientErrorLike } from "@trpc/client";
import type { RouterOutputs } from "../../../api/types";
import type { PreviewInvoice } from "../types";

type PermitType = NonNullable<
	NonNullable<RouterOutputs["locationLookup"]["getLocationLookupEntitiesByIdentifier"]>["permitType"]
>;

export type WizardSettings = PermitType["wizardSettings"];

export type WizardParkingChoice = WizardSettings["choices"][number];

export type WizardLocation = WizardParkingChoice["locations"][number];

export type WizardTimeHelper = WizardParkingChoice["time"];

export type WizardPayment = WizardParkingChoice["payments"];

export type WizardDurationRange = WizardTimeHelper["duration"];

export type WizardTimeRange = WizardTimeHelper["startTime"];

export type WizardDateRange = WizardTimeHelper["startDate"];

export type ToggledWeekdays = WizardDateRange["toggledWeekdays"];

export type ParkingChoiceFormData = {
	parkingChoiceId?: number;
	locationIds?: number[];
	permitIntervals?: {
		startDateTimeISO: string;
		endDateTimeISO?: string | null;
		schedules?: {
			startTime: string;
			endTime: string;
			day: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
		}[];
	}[];
};

export type ParkingDetailsFormData = {
	permitIntervals?: {
		startDateTimeISO: string;
		endDateTimeISO?: string | null;
		schedules?: {
			startTime: string;
			endTime: string;
			day: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
		}[];
	}[];
	vehicles?: {
		id?: number;
		plate: string;
		state: string;
	}[];
	email?: string;
	phoneNumber?: string;
	locationIds?: number[];
};

export type AdditionalInfoFormData = {
	fields?: {
		permitFieldId: number;
		value: string;
		label: string;
	}[];
};

export type CodeFormData = {
	code?: string;
};

export type PermitPostResultData = {
	publicIdentifier: string;
	publicAccessToken?: string | null;
	permitStatus?: string;
};

export type PermitFormData = {
	permitTypeId?: number;
} & ParkingChoiceFormData &
	ParkingDetailsFormData &
	AdditionalInfoFormData &
	CodeFormData & {
		parcsCodes?: {
			code: string;
			locations: { id: number; name: string }[];
		}[];
	} & {
		parkerTermsVersionIds?: number[];
	} & Partial<PermitPostResultData>;

export type PermitPostData = {
	permitTypeId: number;
	parkingChoiceId: number;
	rawIntervals: {
		startDateTimeISO: string;
		endDateTimeISO?: string;
		schedule?: {
			schedules?: {
				startTime: string;
				endTime: string;
				day: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
			}[];
		};
	}[];
	locationIds: number[];
	vehicles: {
		plate: string;
		state: string;
	}[];
	fields: {
		permitFieldId: number;
		value: string;
		label: string;
	}[];
	code?: string;
	email?: string;
	phoneNumber?: string;
	parkerTermsVersionIds?: number[];
};

export const stepIds = {
	parkingTime: "parkingTime",
	parkingDetails: "parkingDetails",
	additionalInfo: "additionalInfo",
	code: "code",
	terms: "terms",
	payment: "payment",
	confirmation: "confirmation",
} as const;

export type StepId = keyof typeof stepIds;

export type ServerError = TRPCClientErrorLike<any> | null | undefined;

export type PortalError = ServerError | null | undefined;

// Option choice made outside the permit wizard.
export type EntrySelection = {
	tenantId?: number;
	permitTypeId?: number;
};

export type RecursivePermitFormData = PermitFormData & {
	prev?: Partial<RecursivePermitFormData>;
};

export type PermitFormStore = {
	formData: RecursivePermitFormData;
	entrySelection?: EntrySelection;
	wizardSettings: WizardSettings;
	steps: StepId[];
	activeStep: StepId;
	invoice?: PreviewInvoice;
	error: PortalError;
	isProcessingPermit: boolean;
	actions: {
		setError: (error: PortalError) => void;
		goNext:
			| ((updater: (data: PermitFormData) => Partial<PermitFormData>) => {
					hasReachedEnd: boolean;
					hasPaymentsStep: boolean;
			  })
			| undefined;
		goToStep: (step: StepId) => void;
		getPermitPostData: () => PermitPostData;
		setIsProcessingPermit: (isProcessingPermit: boolean) => void;
		setAcceptedTerms: (value: Pick<PermitFormData, "parkerTermsVersionIds">) => void;
		prefillAccountData: (value: Pick<PermitFormData, "vehicles" | "email">) => void;
		setEmail: (value: Pick<PermitFormData, "email">) => void;
		setInvoice: (value: { invoice?: PreviewInvoice }) => void;
	};
};
